import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Mail, Lock, LogIn, Loader2, ShieldCheck, ChevronRight } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) navigate('/dashboard');
  };

  return (
    <div className="auth-split-container flex flex-col md:flex-row min-h-screen bg-white">
      {/* LEFT SIDE: Brand & Welcome (hidden on mobile) – SOLID BLUE */}
      <div className="auth-sidebar hidden md:flex md:w-2/5 bg-blue-700 bg-gradient-to-br from-blue-800 to-blue-600 text-white p-10 flex-col justify-between">
        <div>
          <div className="brand-badge inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-xs font-bold tracking-wider mb-6">
            NYM Portal
          </div>
          <h1 className="text-4xl font-bold leading-tight">Welfare Management System</h1>
          <p className="text-white/80 mt-4 text-lg">Secure access for Nairobi Yearly Meeting (Quakers) administrators and members.</p>
          
          <div className="auth-features mt-8 space-y-3">
            <div className="feature-item flex items-center gap-3 text-white/80">
              <ShieldCheck size={20} className="text-white/60" />
              <span>Secure Encrypted Access</span>
            </div>
          </div>
        </div>
        <div className="auth-sidebar-footer text-xs opacity-50">
          © 2026 Friends Church (Quakers) NYM
        </div>
      </div>

      {/* RIGHT SIDE: The Form (full width on mobile) */}
      <div className="auth-form-section flex-1 bg-white p-6 md:p-12 flex items-center justify-center">
        <div className="login-form-wrapper w-full max-w-md mx-auto py-8 md:py-0">
          <div className="md:hidden flex justify-center mb-6">
            <span className="text-2xl font-black text-blue-700">NYM</span>
          </div>
          
          <h2 className="text-3xl font-black text-gray-900 mb-2 text-center md:text-left">Welcome Back</h2>
          <p className="text-gray-500 mb-8 text-center md:text-left">Please enter your credentials to continue</p>

          {error && (
            <div className="error-banner mb-6 flex items-start gap-3 p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">
              <ShieldCheck size={18} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm font-semibold">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="modern-form space-y-6">
            <div className="input-group">
              <label className="block text-sm font-bold text-gray-700 mb-1">Email Address</label>
              <div className="input-wrapper relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <div className="label-row flex justify-between items-center mb-1">
                <label className="text-sm font-bold text-gray-700">Password</label>
                <Link to="/forgot-password" className="text-sm font-bold text-blue-600 hover:underline">
                  Forgot?
                </Link>
              </div>
              <div className="input-wrapper relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  <span>Sign In</span>
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="text-center mt-8 text-sm text-gray-500">
            Don't have an account?{' '}
            <Link to="/register" className="font-bold text-blue-600 hover:underline">
              Create one now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;